import React, { useState, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  Icon,
  IconButton,
  LinearProgress,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Collapse,
  Skeleton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { authService } from "../../../services/auth.service";
import { PasswordChangeFormValues } from "../types/auth.types";
import { AuthError } from "../utils/auth.error";
import { AxiosError } from "axios";

interface ApiErrorResponse {
  success: boolean;
  error: {
    message: string;
  };
}

const validationSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .test(
      "password-requirements",
      "Please meet all password requirements",
      function (value) {
        if (!value) return false;
        return passwordRequirements.every((req) => req.test(value));
      }
    )
    .notOneOf(
      [Yup.ref("currentPassword")],
      "New password must be different from current password"
    ),
  confirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("newPassword")], "Passwords do not match"),
});

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const passwordRequirements: PasswordRequirement[] = [
  {
    label: "At least 6 characters long",
    test: (password) => password.length >= 6,
  },
  {
    label: "Contains uppercase letter",
    test: (password) => /[A-Z]/.test(password),
  },
  {
    label: "Contains lowercase letter",
    test: (password) => /[a-z]/.test(password),
  },
  {
    label: "Contains number",
    test: (password) => /\d/.test(password),
  },
  {
    label: "Contains special character (@$!%*?&)",
    test: (password) => /[@$!%*?&]/.test(password),
  },
];

const calculatePasswordStrength = (password: string): number => {
  if (!password) return 0;
  return (
    (passwordRequirements.filter((req) => req.test(password)).length /
      passwordRequirements.length) *
    100
  );
};

const getPasswordStrengthColor = (strength: number): string => {
  if (strength <= 20) return "#ff4444";
  if (strength <= 40) return "#ffbb33";
  if (strength <= 60) return "#ffbb33";
  if (strength <= 80) return "#00C851";
  return "#007E33";
};

const getPasswordStrengthText = (strength: number): string => {
  if (strength <= 20) return "Very Weak";
  if (strength <= 40) return "Weak";
  if (strength <= 60) return "Fair";
  if (strength <= 80) return "Strong";
  return "Very Strong";
};

export const PasswordChangeForm: React.FC<{ isDialog?: boolean }> = ({
  isDialog = false,
}) => {
  const theme = useTheme();
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Simulate initial load
  React.useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const formik = useFormik<PasswordChangeFormValues>({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setErrorMessage("");
        setSuccessMessage("");

        const response = (await authService.changePassword(
          values.currentPassword,
          values.newPassword
        )) as any;

        if (!response.success) {
          toast.error(response.error?.message || "Failed to change password", {
            position: "top-right",
            autoClose: 4000,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            className: "modern-toast",
            progressClassName: "modern-progress-bar",
          });
          return;
        }

        toast.success("Password changed successfully. Please log in again.", {
          position: "top-right",
          autoClose: 4000,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          className: "modern-toast",
          progressClassName: "modern-progress-bar",
        });
        resetForm();

        setTimeout(() => {
          window.location.href = "/auth/login";
        }, 3000);
      } catch (error: unknown) {
        let errorMessage = "An unexpected error occurred. Please try again.";

        if (error instanceof AuthError) {
          errorMessage = error.message;
        } else if (error instanceof AxiosError && error.response?.data) {
          const errorData = error.response.data as ApiErrorResponse;
          errorMessage = errorData.error?.message || errorMessage;
        }

        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 2000,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          className: "modern-toast",
          progressClassName: "modern-progress-bar",
        });
      }
    },
  });

  const passwordStrength = calculatePasswordStrength(formik.values.newPassword);
  const meetsAllRequirements = passwordStrength === 100;

  const passwordRequirementsList = useMemo(
    () => (
      <List
        dense
        sx={{
          mt: 1,
          backgroundColor: alpha(theme.palette.background.default, 0.5),
          borderRadius: 1,
          p: 1.5,
        }}
      >
        {passwordRequirements.map((requirement, index) => {
          const isMet = requirement.test(formik.values.newPassword);
          return (
            <ListItem key={index} sx={{ py: 0.3 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Icon
                  style={{
                    color: isMet
                      ? theme.palette.success.main
                      : theme.palette.text.disabled,
                    fontSize: "1.2rem",
                  }}
                >
                  {isMet ? "check_circle" : "radio_button_unchecked"}
                </Icon>
              </ListItemIcon>
              <ListItemText
                primary={requirement.label}
                primaryTypographyProps={{
                  variant: "caption",
                  color: isMet ? "textPrimary" : "textSecondary",
                  fontWeight: isMet ? 500 : 400,
                }}
              />
            </ListItem>
          );
        })}
      </List>
    ),
    [formik.values.newPassword, theme]
  );

  if (isLoading) {
    return (
      <Box sx={{ p: isDialog ? 0 : 3 }}>
        <Skeleton
          variant="rectangular"
          height={56}
          sx={{ mb: 2, borderRadius: 1 }}
        />
        <Skeleton
          variant="rectangular"
          height={56}
          sx={{ mb: 2, borderRadius: 1 }}
        />
        <Skeleton
          variant="rectangular"
          height={56}
          sx={{ mb: 2, borderRadius: 1 }}
        />
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: isDialog ? 0 : 3,
        maxWidth: isDialog ? "none" : "450px",
        margin: isDialog ? 0 : "auto",
      }}
    >
      <Collapse in={Boolean(successMessage || errorMessage)} sx={{ mb: 3 }}>
        {successMessage && (
          <Alert
            severity="success"
            sx={{
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.success.main, 0.1),
            }}
          >
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert
            severity="error"
            sx={{
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.error.main, 0.1),
            }}
          >
            {errorMessage}
          </Alert>
        )}
      </Collapse>

      <form onSubmit={formik.handleSubmit}>
        <TextField
          fullWidth
          variant="outlined"
          id="currentPassword"
          name="currentPassword"
          label="Current Password"
          type={showCurrentPassword ? "text" : "password"}
          value={formik.values.currentPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.currentPassword &&
            Boolean(formik.errors.currentPassword)
          }
          helperText={
            formik.touched.currentPassword && formik.errors.currentPassword
          }
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  edge="end"
                  size="small"
                >
                  <Icon style={{ fontSize: "1.2rem" }}>
                    {showCurrentPassword ? "visibility_off" : "visibility"}
                  </Icon>
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          variant="outlined"
          id="newPassword"
          name="newPassword"
          label="New Password"
          type={showNewPassword ? "text" : "password"}
          value={formik.values.newPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.newPassword && Boolean(formik.errors.newPassword)
          }
          helperText={formik.touched.newPassword && formik.errors.newPassword}
          sx={{ mb: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                  size="small"
                >
                  <Icon style={{ fontSize: "1.2rem" }}>
                    {showNewPassword ? "visibility_off" : "visibility"}
                  </Icon>
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Collapse in={Boolean(formik.values.newPassword)} sx={{ mb: 2 }}>
          <Fade in={Boolean(formik.values.newPassword)}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength}
                  sx={{
                    flexGrow: 1,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: alpha(theme.palette.grey[300], 0.5),
                    "& .MuiLinearProgress-bar": {
                      backgroundColor:
                        getPasswordStrengthColor(passwordStrength),
                      borderRadius: 3,
                      transition: theme.transitions.create(
                        ["transform", "background-color"],
                        {
                          duration: theme.transitions.duration.shorter,
                        }
                      ),
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    ml: 2,
                    color: getPasswordStrengthColor(passwordStrength),
                    fontWeight: 500,
                    minWidth: 70,
                  }}
                >
                  {getPasswordStrengthText(passwordStrength)}
                </Typography>
              </Box>
              {passwordRequirementsList}
            </Box>
          </Fade>
        </Collapse>

        <TextField
          fullWidth
          variant="outlined"
          id="confirmPassword"
          name="confirmPassword"
          label="Confirm New Password"
          type={showConfirmPassword ? "text" : "password"}
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.confirmPassword &&
            Boolean(formik.errors.confirmPassword)
          }
          helperText={
            formik.touched.confirmPassword && formik.errors.confirmPassword
          }
          sx={{ mb: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  size="small"
                >
                  <Icon style={{ fontSize: "1.2rem" }}>
                    {showConfirmPassword ? "visibility_off" : "visibility"}
                  </Icon>
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <LoadingButton
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          loading={formik.isSubmitting}
          disabled={!formik.isValid || !formik.dirty || !meetsAllRequirements}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            py: 1.2,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          Change Password
        </LoadingButton>
      </form>
    </Box>
  );
};
